import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MailOutline, PhoneOutlined, ExitToApp } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import TimelineItem from './components/TimelineItem';
import ReplySection from './components/ReplySection';
import { ticketApis } from '../../../Data/axios';
import { API_CONFIG, UPLOAD_LIMITS, SUCCESS_MESSAGES } from './utils/config';
import {
  compressFiles,
  validateFileSize,
  isFileTypeAllowed,
  getFileUrl,
  storeUploadError
} from './utils/fileHandler';
import { formatDateTimeWithTimezone } from './utils/dataMapping';
import { getAuthToken } from '../../../utils/authUtils';

const ActivityTicket = ({ ticketId, baseUrl, uploadBaseUrl, showErrorAlert, showSuccessAlert }) => {
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingReply, setSendingReply] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ show: false, progress: 0 });
  const [previewFile, setPreviewFile] = useState(null);
  const [agentDetails, setAgentDetails] = useState(null);
  const navigate = useNavigate();
  const hasMounted = useRef(false);
  const scrollContainerRef = useRef(null);
  const lastPageRef = useRef(1);
  const hasMoreDataRef = useRef(true);
  const isAppendingRef = useRef(false);

  // Fetch agent details from getEmployeesByID
  const fetchAgentDetails = useCallback(async (agentId) => {
    if (!agentId) return;
    try {
      const response = await ticketApis.getAgentDetails(agentId);
      if (response.success && response.data) {
        // The API returns { data: { name, empid, image, role, role_name, status, mobile, email } }
        const userData = response.data.data || response.data;
        setAgentDetails(userData);
      }
    } catch (err) {
      console.error('Error fetching agent details:', err);
    }
  }, []);

  // Fetch ticket history on component mount
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      fetchHistoryData(1);
    }
  }, [ticketId]);

  // Auto-scroll to top on initial load (newest first order)
  useEffect(() => {
    if (isAppendingRef.current) {
      isAppendingRef.current = false;
      return;
    }
    if (scrollContainerRef.current && historyData?.histories?.length > 0) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [historyData?.histories?.length]);

  // Fetch agent details when agent_id is available
  useEffect(() => {
    if (historyData?.agent?.agent_id) {
      fetchAgentDetails(historyData.agent.agent_id);
    }
  }, [historyData?.agent?.agent_id, fetchAgentDetails]);

  // Fetch history data
  const fetchHistoryData = useCallback(async (page = 1, append = false) => {
    setLoading(true);
    setError(null);

    console.log('Fetching ticket history for ID:', ticketId, 'Page:', page);

    try {
      const response = await ticketApis.getTicketHistory(ticketId, page, 10);

      console.log('History API response:', response);
      console.log('Response success:', response.success);
      console.log('Response data:', response.data);

      if (response.success && response.data) {
        const { agent, pagination, data: histories } = response.data;

        console.log('Setting history data:', { agent, pagination, histories });
        console.log('Number of history items:', histories?.length);

        // Validate and sanitize history data
        const validHistories = Array.isArray(histories) ? histories.filter(h => h && typeof h === 'object') : [];

        console.log('Valid history items:', validHistories.length);

        setHistoryData(prev => {
          if (append && prev?.histories) {
            isAppendingRef.current = true;
            return {
              agent: agent || prev.agent,
              pagination: pagination,
              histories: [...prev.histories, ...validHistories]
            };
          }
          return { agent, pagination, histories: validHistories };
        });

        // Update pagination tracking
        lastPageRef.current = page;
        hasMoreDataRef.current = pagination?.current_page < pagination?.last_page;

      } else {
        console.error('API returned unsuccessful:', response);
        setError('Failed to load ticket activity');
      }
    } catch (err) {
      console.error('Error fetching ticket history:', err);
      console.error('Error details:', err.message);
      console.error('Error stack:', err.stack);
      setError('An error occurred while loading activity');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  // Handle file preview
  const handleFilePreview = useCallback((filePath) => {
    const fullUrl = `${uploadBaseUrl}${filePath}`;
    setPreviewFile(fullUrl);
  }, [uploadBaseUrl]);

  // Handle file download
  const handleFileDownload = useCallback((filePath, fileName) => {
    const fullUrl = `${uploadBaseUrl}${filePath}`;
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = fileName || 'download';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [uploadBaseUrl]);

  // Handle reply submission
  const handleReply = useCallback(async (text, files) => {
    if (!text.trim() && files.length === 0) {
      return;
    }

    setSendingReply(true);
    setUploadProgress({ show: true, progress: 0 });

    try {
      // Validate files
      const validationResults = files.map(file => {
        const sizeCheck = validateFileSize(file, UPLOAD_LIMITS.MAX_FILE_SIZE);
        const typeCheck = isFileTypeAllowed(file, ['image', 'pdf', 'document', 'audio', 'video']);

        if (!sizeCheck.valid || !typeCheck.valid) {
          const errorMsg = sizeCheck.error || typeCheck.error;
          return { file: file, valid: false, error: errorMsg };
        }
        return { file: file, valid: true, error: null };
      });

      const invalidFiles = validationResults.filter(r => !r.valid);
      if (invalidFiles.length > 0) {
        showErrorAlert?.({
          message: `Some files failed validation: ${invalidFiles.map(f => f.error).join(', ')}`,
          timestamp: Date.now()
        });
        setSendingReply(false);
        setUploadProgress({ show: false, progress: 0 });
        return;
      }

      // Compress images
      const compressedFiles = await compressFiles(files);

      // Prepare FormData with correct field names
      const formData = new FormData();
      formData.append('ticket_id', ticketId);
      formData.append('text', text);
      formData.append('status', '1'); // Default status for replies
      compressedFiles.forEach(file => formData.append('files[]', file));

      // Upload with XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      const uploadUrl = `${baseUrl}/addTicketHistory`;

      xhr.open('POST', uploadUrl, true);

      // Get token using centralized utility
      const token = getAuthToken();

      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      } else {
        console.error('No authentication token available');
        showErrorAlert?.('Authentication required. Please login again.');
        setSendingReply(false);
        setUploadProgress({ show: false, progress: 0 });
        return;
      }

      // Track upload progress
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress({
            show: true,
            progress: percentComplete
          });
        }
      };

      xhr.onload = () => {
        setSendingReply(false);
        setUploadProgress({ show: false, progress: 0 });

        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            console.log('Reply submission response:', response);
            if (response.success || response.code === '1602') {
              // Refresh history to show new reply
              fetchHistoryData(1, false); // Reload from page 1
              console.log('Refreshing history after reply');
              showSuccessAlert?.(SUCCESS_MESSAGES.REPLY_SENT);
            } else {
              const errorMsg = response.message || 'Failed to send reply';
              console.error('Reply submission failed:', errorMsg);
              showErrorAlert?.({
                message: errorMsg,
                timestamp: Date.now()
              });
              storeUploadError(errorMsg);
            }
          } catch (parseError) {
            console.error('Error parsing upload response:', parseError);
            showErrorAlert?.({
              message: 'Failed to process upload response',
              timestamp: Date.now()
            });
          }
        } else if (xhr.status === 401) {
          // Handle authentication error
          console.error('Authentication failed for ticket history upload');
          showErrorAlert?.('Authentication failed. Please login again.');
          storeUploadError('Authentication failed. Please login again.');
        } else {
          let errorMsg = `Upload failed with status: ${xhr.status}`;
          try {
            const errResponse = JSON.parse(xhr.responseText);
            if (errResponse.message) {
              errorMsg = errResponse.message;
            } else if (errResponse.error) {
              errorMsg = errResponse.error;
            } else if (errResponse.errors) {
              errorMsg = Object.values(errResponse.errors).flat().join(', ');
            }
          } catch (e) {}
          console.error('Upload error:', errorMsg, xhr.responseText);
          showErrorAlert?.({
            message: errorMsg,
            timestamp: Date.now()
          });
          storeUploadError(errorMsg);
        }
      };

      xhr.onerror = () => {
        setSendingReply(false);
        setUploadProgress({ show: false, progress: 0 });
        const errorMsg = 'Network error occurred while uploading';
        showErrorAlert?.({
          message: errorMsg,
          timestamp: Date.now()
        });
        storeUploadError(errorMsg);
      };

      xhr.send(formData);
    } catch (error) {
      console.error('Error sending reply:', error);
      setSendingReply(false);
      setUploadProgress({ show: false, progress: 0 });
      showErrorAlert?.({
        message: 'An error occurred while sending reply',
        timestamp: Date.now()
      });
    }
  }, [ticketId, baseUrl, showErrorAlert, fetchHistoryData]);

  // Handle scroll to load more
  const handleScroll = useCallback(() => {
    if (loading || !hasMoreDataRef.current || !scrollContainerRef.current) {
      return;
    }

    const container = scrollContainerRef.current;
    const scrollPercentage = (container.scrollTop + container.clientHeight) / container.scrollHeight;

    // Load more when user scrolls near bottom (within 100px)
    if (container.scrollHeight - (container.scrollTop + container.clientHeight) < 100) {
      const nextPage = lastPageRef.current + 1;
      fetchHistoryData(nextPage, true);
    }
  }, [loading, fetchHistoryData]);

  // Get requester profile info
  const getRequesterProfile = () => {
    // Prefer fetched agentDetails (from getEmployeesByID), fall back to historyData.agent
    const agent = agentDetails || historyData?.agent;

    if (!agent) {
      return {
        name: 'Unknown',
        initials: 'U',
        id: 'N/A',
        email: 'N/A',
        phone: 'N/A',
        image: null
      };
    }

    return {
      name: agent.name || agent.agent_name || 'Support Agent',
      initials: agent.name ? agent.name.substring(0, 2).toUpperCase() : 'SA',
      id: agent.empid || agent.id || agent.agent_id || 'N/A',
      email: agent.email || 'info@roadpilot.co.in',
      phone: agent.mobile || agent.phone || '+91 99999 99999',
      image: agent.image || agent.agent_image
    };
  };

  const requesterProfile = getRequesterProfile();

  // Render loading state
  if (loading && !historyData) {
    return (
      <div className="flex flex-col gap-4 font-ubuntu w-full px-4 border-box">
        {/* Loading skeleton for profile */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse"></div>
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded animate-pulse mb-2 w-32"></div>
            <div className="h-3 bg-gray-200 rounded animate-pulse w-48"></div>
          </div>
        </div>

        {/* Loading skeleton for timeline */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse">
                <div className="flex gap-4">
                  <div className="w-9 h-9 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                    <div className="h-16 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="flex flex-col gap-4 font-ubuntu w-full px-4 border-box">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-bold text-red-800 mb-2">Error Loading Activity</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => fetchHistoryData(1, false)}
            className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 font-ubuntu w-full px-4 border-box">
      {/* Requester Profile Badge */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex flex-wrap items-center justify-between gap-4 w-full">
        <div className="flex items-center gap-4">
          <div className="relative">
            {requesterProfile.image ? (
              <img
                src={`${uploadBaseUrl}${requesterProfile.image}`}
                alt={requesterProfile.name}
                className="w-12 h-12 rounded-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `<div class="bg-gray-200 text-gray-500 font-bold flex items-center justify-center rounded-full w-12 h-12 text-sm">${requesterProfile.initials}</div>`;
                }}
              />
            ) : (
              <div className="bg-gray-200 text-gray-500 font-bold flex items-center justify-center rounded-full w-12 h-12 text-sm">
                {requesterProfile.initials}
              </div>
            )}
            {/* Online indicator dot */}
            <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full"></div>
          </div>
          <div>
            <p className="text-gray-800 font-bold text-sm">{requesterProfile.name}</p>
            <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
              <span className="flex items-center gap-1 font-medium">
                <span className="text-gray-400">ID:</span> <span className="text-gray-700">{requesterProfile.id}</span>
              </span>
              <span className="flex items-center gap-1 font-medium">
                <MailOutline fontSize="small" sx={{ fontSize: 14 }} className="text-gray-400" /> <span className="text-gray-700">{requesterProfile.email}</span>
              </span>
              <span className="flex items-center gap-1 font-medium">
                <PhoneOutlined fontSize="small" sx={{ fontSize: 14 }} className="text-gray-400" /> <span className="text-gray-700">{requesterProfile.phone}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Section */}
      <div
        ref={scrollContainerRef}
        className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 w-full mb-2 max-h-[500px] overflow-y-auto"
        onScroll={handleScroll}
      >
        <h3 className="text-gray-800 font-bold text-sm mb-4">Timeline</h3>

        <div className="relative">
          {/* Vertical line running down timeline */}
          <div className="absolute top-4 bottom-4 left-4 w-0.5 bg-gray-200"></div>

          <div className="space-y-2">
            {historyData?.histories && historyData.histories.length > 0 ? (
              historyData.histories.map((historyItem, index) => (
                <TimelineItem
                  key={historyItem.id || index}
                  message={historyItem}
                  isFirst={index === 0}
                  isLast={index === historyData.histories.length - 1}
                  onFilePreview={handleFilePreview}
                  onFileDownload={handleFileDownload}
                />
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">No activity yet</p>
              </div>
            )}

            {/* Loading indicator for more data */}
            {loading && historyData?.histories?.length > 0 && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 border-t-2 border-blue-500 mx-auto"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Progress */}
      {uploadProgress.show && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 w-full mb-2">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-900 border-t-2 border-blue-500"></div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-blue-800">Uploading...</span>
                <span className="text-sm text-blue-600">{uploadProgress.progress}%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress.progress}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reply Section */}
      <ReplySection
        onReply={handleReply}
        disabled={sendingReply}
        loading={sendingReply}
      />

      {/* File Preview Modal */}
      {previewFile && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewFile(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-800">File Preview</h3>
              <button
                onClick={() => setPreviewFile(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-100">
              {previewFile.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i) ? (
                <img src={previewFile} alt="Preview" className="max-w-full max-h-[70vh] object-contain rounded" />
              ) : previewFile.match(/\.(pdf)$/i) ? (
                <iframe src={previewFile} className="w-full h-[70vh]" title="PDF Preview" />
              ) : previewFile.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i) ? (
                <video src={previewFile} controls className="max-w-full max-h-[70vh]" />
              ) : previewFile.match(/\.(mp3|wav|ogg|aac|flac)$/i) ? (
                <audio src={previewFile} controls className="w-full" />
              ) : (
                <div className="text-center p-8">
                  <p className="text-gray-500 mb-4">Preview not available for this file type</p>
                  <button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = previewFile;
                      link.download = previewFile.split('/').pop();
                      link.target = '_blank';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 transition-colors"
                  >
                    Download File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Exit Button Segment */}
      <div className="flex justify-end w-full pb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-6 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded-md font-medium text-sm transition-colors shadow-sm"
        >
          <ExitToApp fontSize="small" />
          Exit
        </button>
      </div>
    </div>
  );
};

export default ActivityTicket;
